import { FILTER_VERTEX_GLSL } from "./filterVertex";
import { EXPOSURE_FRAGMENT_GLSL } from "./ExposureFilter";
import { SHINE_SWEEP_FRAGMENT_GLSL } from "./ShineSweepFilter";
import { colorOverlayFragmentGlsl } from "./ColorOverlayFilter";

export interface FilterProgramCheck {
  name: string;
  ok: boolean;
  log: string;
}

const HEADER = "#version 300 es\nprecision highp float;\n";

function compile(gl: WebGL2RenderingContext, type: number, source: string): { shader: WebGLShader; ok: boolean; log: string } {
  const shader = gl.createShader(type)!;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  const ok = gl.getShaderParameter(shader, gl.COMPILE_STATUS) as boolean;
  return { shader, ok, log: gl.getShaderInfoLog(shader) ?? "" };
}

/**
 * Dev-only: compiles + links every shell filter program on a raw WebGL2
 * context and returns the driver's info logs, so a shader regression is a
 * readable message instead of Pixi's generic "Could not initialize shader".
 */
export function checkFilterPrograms(gl: WebGL2RenderingContext): FilterProgramCheck[] {
  const programs: Array<[string, string]> = [
    ["exposure", EXPOSURE_FRAGMENT_GLSL],
    ["shine-sweep", SHINE_SWEEP_FRAGMENT_GLSL],
    ["color-overlay-darken", colorOverlayFragmentGlsl("darken")],
    ["color-overlay-softLight", colorOverlayFragmentGlsl("softLight")],
  ];
  return programs.map(([name, fragment]) => {
    const vs = compile(gl, gl.VERTEX_SHADER, HEADER + FILTER_VERTEX_GLSL);
    const fs = compile(gl, gl.FRAGMENT_SHADER, HEADER + fragment);
    let ok = vs.ok && fs.ok;
    let log = [vs.log && `vertex: ${vs.log}`, fs.log && `fragment: ${fs.log}`].filter(Boolean).join("\n");
    if (ok) {
      const program = gl.createProgram()!;
      gl.attachShader(program, vs.shader);
      gl.attachShader(program, fs.shader);
      gl.linkProgram(program);
      ok = gl.getProgramParameter(program, gl.LINK_STATUS) as boolean;
      if (!ok) log += `\nlink: ${gl.getProgramInfoLog(program) ?? ""}`;
      gl.deleteProgram(program);
    }
    gl.deleteShader(vs.shader);
    gl.deleteShader(fs.shader);
    return { name, ok, log: log.trim() };
  });
}
